import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import projectQueries from "../../../../requests/queries/ProjectsQuesries";
import MovableAndResizableComponent from "./components/MovableAndResizableComponent";
import AddPageComponent from "./components/AddPageComponent";
import PageContextMenu from "./components/PageContextMenu";
import "./editor.css";
import PagesQueries from "../../../../requests/queries/PagesQueries";
import { CellsQueries } from "../../../../requests/queries/CellsQueries";
import { TextCell, ImageCell, VideoCell, ButtonCell, CarouselCell, FormCell, IframeCell, HeaderCell, MenuCell, HtmlCell, CellWrapper, ScheduleCell, PluracoreCell, ClockCell, ReviewCell, MapCell, CommandCell } from "commun-cells";
import "commun-cells/styles/cells.css";
import TextCellProperties from "./components/cell_type/TextCellProperties";
import FormCellProperties from "./components/cell_type/FormCellProperties";
import ImageCellProperties from "./components/cell_type/ImageCellProperties";
import ColorPicker from "./components/picker/ColorPicker";
import ImagePicker from "./components/picker/ImagePicker";
import VideoPicker from "./components/picker/VideoPicker";
import IframeCellProperties from "./components/cell_type/IframeCellProperties";
import CarouselCellProperties from "./components/cell_type/CarouselCellProperties";
import VideoCellProperties from "./components/cell_type/VideoCellProperties";
import ButtonCellProperties from "./components/cell_type/ButtonCellProperties";
import HeaderCellProperties from "./components/cell_type/HeaderCellProperties";
import HtmlCellProperties from "./components/cell_type/HtmlCellProperties";
import ProjectsQuesries from "../../../../requests/queries/ProjectsQuesries";
import MenuCellProperties from "./components/cell_type/MenuCellProperties";
import ScheduledCellProperties from "./components/cell_type/ScheduledCellProperties";
import ClockCellProperties from "./components/cell_type/ClockCellProperties";
import ReviewCellProperties from "./components/cell_type/ReviewCellProperties";
import MapCellProperties from "./components/cell_type/MapCellProperties";
import CommandCellProperties from "./components/cell_type/CommandCellProperties";

export default function Editor() {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(null);
    const [selectedCell, setSelectedCell] = useState(null);
    const [zoom, setZoom] = useState(100);
    const [contextMenu, setContextMenu] = useState(null);
    const [contextSidebar, setContextSidebar] = useState('pages');

    const default_open_page = new URLSearchParams(window.location.search).get('page');

    useEffect(() => {
        getProject();
    }, [id]);

    const getProject = async () => {
        setIsLoading(true);
        try {
            const data = await projectQueries.exportProject(id);
            setProject(data);
            if (data.pages && data.pages.length > 0) {
                setCurrentPage(data.pages[0]);
            }
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 300);
        }
    };

    const handleAddPage = async (pageName) => {
        const page = await PagesQueries.createPage(id, pageName);
        setCurrentPage(page);
        setProject({ ...project, pages: [...project.pages, page] });
    };

    const handleRenamePage = async (pageId, newName) => {
        await PagesQueries.updatePage(pageId, newName);
        const updatedPages = project.pages.map(p => 
            p.id === pageId ? { ...p, name: newName } : p
        );
        setProject({ ...project, pages: updatedPages });
        if (currentPage?.id === pageId) {
            setCurrentPage({ ...currentPage, name: newName });
        }
    };

    const handleMakeMainPage = async (pageId) => {
        await ProjectsQuesries.updateProject(project.id, { main_page_id: pageId });
        setProject({ ...project, main_page_id: pageId });
    }

    const handleDeletePage = async (pageId) => {
        await PagesQueries.deletePage(pageId);
        const updatedPages = project.pages.filter(p => p.id !== pageId);
        setProject({ ...project, pages: updatedPages });
        if (currentPage?.id === pageId) {
            setCurrentPage(updatedPages[0] || null);
        }
    };

    const handleOpenContextMenu = (e, page) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setContextMenu({
            page,
            position: { x: rect.right + 5, y: rect.top }
        });
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handleSelectCell = (cellId) => {
        setSelectedCell(cellId);
    };

    const handleDeselectAll = () => {
        setSelectedCell(null);
    };

    const handlePositionChange = async (cellId, position) => {
        const cell = currentPage?.cells?.find(c => c.id === cellId);
        if (!cell) return;
        
        // Utiliser cell_id pour l'API (ID de la table cells), pas id (ID de la table spécifique)
        const actualCellId = cell.cell_id;
        
        // Les positions sont déjà en unités de base (le scale est géré par MovableAndResizable)
        const realPosition = {
            x: Math.round(position.x),
            y: Math.round(position.y)
        };
        
        try {
            await CellsQueries.updatePositionAndSize(actualCellId, cell.type, realPosition, null);
            
            const updatedCells = currentPage.cells.map(c => 
                c.id === cellId 
                    ? { ...c, position_x: realPosition.x, position_y: realPosition.y }
                    : c
            );
            setCurrentPage({ ...currentPage, cells: updatedCells });
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de la position:", error);
        }
    };

    const handleSizeChange = async (cellId, size, position) => {
        const cell = currentPage?.cells?.find(c => c.id === cellId);
        if (!cell) return;
        
        const actualCellId = cell.cell_id;
        
        const realSize = {
            width: Math.round(size.width),
            height: Math.round(size.height)
        };
        
        const realPosition = {
            x: Math.round(position.x),
            y: Math.round(position.y)
        };
        
        try {
            await CellsQueries.updatePositionAndSize(actualCellId, cell.type, realPosition, realSize);
            
            const updatedCells = currentPage.cells.map(c => 
                c.id === cellId 
                    ? { 
                        ...c, 
                        size_x: realSize.width, 
                        size_y: realSize.height,
                        position_x: realPosition.x,
                        position_y: realPosition.y
                    }
                    : c
            );
            setCurrentPage({ ...currentPage, cells: updatedCells });
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de la taille:", error);
        }
    };

    const handlePropertyPositionChange = async (actualCellId, position) => {
        const cell = currentPage?.cells?.find(c => c.cell_id === actualCellId);
        if (!cell) return;
        
        try {
            await CellsQueries.updatePositionAndSize(actualCellId, cell.type, position, null);
            
            const updatedCells = currentPage.cells.map(c => 
                c.cell_id === actualCellId 
                    ? { ...c, position_x: Math.round(position.x), position_y: Math.round(position.y) }
                    : c
            );
            setCurrentPage({ ...currentPage, cells: updatedCells });
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de la position:", error);
        }
    };

    const handlePropertySizeChange = async (actualCellId, size) => {
        const cell = currentPage?.cells?.find(c => c.cell_id === actualCellId);
        if (!cell) return;
        
        const position = { x: cell.position_x, y: cell.position_y };
        
        try {
            await CellsQueries.updatePositionAndSize(actualCellId, cell.type, position, size);
            
            const updatedCells = currentPage.cells.map(c => 
                c.cell_id === actualCellId 
                    ? { ...c, size_x: Math.round(size.width), size_y: Math.round(size.height) }
                    : c
            );
            setCurrentPage({ ...currentPage, cells: updatedCells });
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de la taille:", error);
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 10, 200));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 10, 25));
    };

    const handleZoomReset = () => {
        setZoom(100);
    };

    const handleAddCell = async (cellType) => {
        const cell = await CellsQueries.create(currentPage.id, cellType);
        setCurrentPage({ ...currentPage, cells: [...currentPage.cells, cell] });
    };

    const getPreviewDimensions = () => {
        if (!project?.screen_size) return { width: 800, height: 600 };
        
        const baseSize = 50;
        return {
            width: project.screen_size.ratio_x * baseSize,
            height: project.screen_size.ratio_y * baseSize
        };
    };

    const handleDeleteCell = async (cellId) => {
        try {
            await CellsQueries.delete(cellId);
            const updatedCells = currentPage.cells.filter(c => c.cell_id !== cellId);
            setCurrentPage({ ...currentPage, cells: updatedCells });
        } catch (error) {
            console.error("Erreur lors de la suppression de la cellule:", error);
        }
    };

    const handleUpdateCellProperties = async (cellId, properties) => {
        try {
            const updatedCell = await CellsQueries.update(cellId, properties);
            const updatedCells = currentPage.cells.map(c => {
                const matches =
                    c.cell_id === cellId ||
                    c.id === cellId ||
                    (updatedCell?.cell_id && c.cell_id === updatedCell.cell_id);
                if (!matches) return c;
                return {
                    ...c,
                    ...updatedCell,
                    cell_id: updatedCell.cell_id ?? c.cell_id ?? c.id,
                    id: updatedCell.id ?? c.id,
                };
            });
            setCurrentPage({ ...currentPage, cells: updatedCells });
        } catch (error) {
            console.error("Erreur lors de la mise à jour des propriétés de la cellule:", error);
        }
    };

    useEffect(() => {
        if (default_open_page && project?.pages) {
            setCurrentPage(project.pages.find(p => p.id === default_open_page));
        }
    }, [default_open_page]);

    if (isLoading) {
        return (
            <div className="editor-page">
                <div className="editor-loading">
                    <div className="loading-spinner"></div>
                    <span className="loading-text">Chargement de l'éditeur...</span>
                </div>
            </div>
        );
    }

    const previewDimensions = getPreviewDimensions();

    return (
        <div className="editor-page">
            {/* Header / Toolbar */}
            <header className="editor-header">
                <div className="editor-header-left">
                    <Link to={`/project/${id}`} className="editor-back-btn" title="Retour au projet">
                        ←
                    </Link>
                    <div className="editor-project-name">
                        <div className="editor-project-icon">🖥️</div>
                        <span className="editor-project-title">{project?.name || "Projet"}</span>
                    </div>
                </div>

                <div className="editor-header-center">
                    
                </div>

                <div className="editor-header-right">
                    <div className="editor-zoom-control">
                        <button className="editor-zoom-btn" onClick={handleZoomOut} title="Zoom arrière">
                            −
                        </button>
                        <span 
                            className="editor-zoom-value" 
                            onClick={handleZoomReset}
                            style={{ cursor: 'pointer' }}
                            title="Réinitialiser le zoom"
                        >
                            {zoom}%
                        </span>
                        <button className="editor-zoom-btn" onClick={handleZoomIn} title="Zoom avant">
                            +
                        </button>
                    </div>
                    <button onClick={()=>{window.location.reload()}} className="editor-save-btn">
                        💾 Recharger
                    </button>
                </div>
            </header>

            <div className="editor-container">
                {
                    contextSidebar === 'pages' && (
                            <aside className="editor-sidebar">
                            <div className="editor-sidebar-header">
                                <span onClick={() => setContextSidebar('pages')} className={`editor-sidebar-title ${contextSidebar === 'pages' ? 'active' : ''}`}>Pages</span>
                                <span onClick={() => setContextSidebar('cells')} className={`editor-sidebar-title ${contextSidebar === 'cells' ? 'active' : ''}`}>Cells</span>
                            </div>
                            <div className="editor-sidebar-content">
                                <AddPageComponent 
                                    onAddPage={handleAddPage}
                                    projectId={id}
                                />
                                
                                <nav className="pages-nav">
                                    {project?.pages?.sort((a, b) => a.index - b.index).sort((a, b) => a.id === project?.main_page_id ? -1 : b.id === project?.main_page_id ? 1 : 0).map((page) => (
                                        <div 
                                            key={page.id} 
                                            className={`page-nav-item ${currentPage?.id === page.id ? 'active' : ''}`}
                                            onClick={() => {
                                                setCurrentPage(page);
                                                setSelectedCell(null);
                                            }}
                                        >
                                            <div className="page-nav-thumbnail">
                                                📄
                                            </div>
                                            <div className="page-nav-info">
                                                <span className="page-nav-name">{page.name}</span>
                                                <span className="page-nav-meta">
                                                    {" "} {page.cells?.length || 0} cellule{(page.cells?.length || 0) > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <button 
                                                className="page-nav-menu" 
                                                title="Options"
                                                onClick={(e) => handleOpenContextMenu(e, page)}
                                            >
                                                ⋮
                                            </button>
                                            {
                                                page.id === project?.main_page_id && (
                                                    <span className="page-nav-main-page">🏠</span>
                                                )
                                            }
                                        </div>
                                    ))}
                                </nav>

                                {/* Menu contextuel */}
                                {contextMenu && (
                                    <PageContextMenu
                                        onMakeMainPage={handleMakeMainPage}
                                        page={contextMenu.page}
                                        position={contextMenu.position}
                                        onRename={handleRenamePage}
                                        onDelete={handleDeletePage}
                                        onClose={handleCloseContextMenu}
                                    />
                                )}
                            </div>
                        </aside>
                    )
                }
                {
                    contextSidebar === 'cells' && (
                        <aside className="editor-sidebar">
                            <div className="editor-sidebar-header">
                                <span onClick={() => setContextSidebar('pages')} className={`editor-sidebar-title ${contextSidebar === 'pages' ? 'active' : ''}`}>Pages</span>
                                <span onClick={() => setContextSidebar('cells')} className={`editor-sidebar-title ${contextSidebar === 'cells' ? 'active' : ''}`}>Cells</span>
                            </div>
                            <div className="editor-sidebar-content">
                                <div className="cell-nav-item">
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('text')}>
                                        <span>
                                            Text
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('image')}>
                                        <span>
                                            Image
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('video')}>
                                        <span>
                                            Video
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('button')}>
                                        <span>
                                            Button
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('form')}>
                                        <span>
                                            Form
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('carousel')}>
                                        <span>
                                            Carousel
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('iframe')}>
                                        <span>
                                            Iframe
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('header')}>
                                        <span>
                                            Header
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('html')}>
                                        <span>
                                            Html
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('scheduled')}>
                                        <span>
                                            Scheduled
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('menu')}>
                                        <span>
                                            Menu
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('clock')}>
                                        <span>
                                            Clock
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('pluracore')}>
                                        <span>
                                            Pluracore
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('review')}>
                                        <span>
                                            Review
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('map')}>
                                        <span>
                                            Map
                                        </span>
                                    </div>
                                    <div className="cell-nav-thumbnail" onClick={() => handleAddCell('commands')}>
                                        <span>
                                            Command
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    )
                }
                {/* Zone Canvas */}
                <main className="editor-main">
                    <div 
                        className="editor-canvas-wrapper"
                        onClick={handleDeselectAll}
                    >
                        <div className="page-preview">
                            <div 
                                className="preview-screen-wrapper"
                                style={{
                                    width: `${previewDimensions.width * (zoom / 100)}px`,
                                    height: `${previewDimensions.height * (zoom / 100)}px`,
                                }}
                            >
                                <div 
                                    id="preview-screen" 
                                    style={{
                                        width: `${previewDimensions.width}px`,
                                        height: `${previewDimensions.height}px`,
                                        transform: `scale(${zoom / 100})`,
                                        transformOrigin: 'top left',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        backgroundColor: currentPage?.background_type === 'color'
                                            ? currentPage?.background_color
                                            : currentPage?.background_type === 'video'
                                                ? '#000000'
                                                : '#ffffff',
                                        backgroundImage: currentPage?.background_type === 'image' ? `url(${currentPage?.background_image_uri})` : 'none',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                    }}
                                    onClick={(e) => {setSelectedCell(null); e.stopPropagation()}}
                                >
                                    {currentPage?.background_type === 'video' && currentPage?.background_video_uri && (
                                        <video
                                            src={currentPage.background_video_uri}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                zIndex: 0,
                                                pointerEvents: 'none',
                                            }}
                                        />
                                    )}
                                    {currentPage ? (
                                        currentPage.cells && currentPage.cells.length > 0 ? (
                                            currentPage.cells.map((cell) => (
                                                <MovableAndResizableComponent
                                                    key={cell.id}
                                                    cellId={cell.id}
                                                    cellType={cell.type}
                                                    initialX={cell.position_x}
                                                    initialY={cell.position_y}
                                                    initialWidth={cell.size_x || 200}
                                                    initialHeight={cell.size_y || 100}
                                                    isSelected={selectedCell === cell.id}
                                                    onSelect={handleSelectCell}
                                                    onPositionChange={handlePositionChange}
                                                    onSizeChange={handleSizeChange}
                                                    scale={zoom / 100}
                                                >
                                                    <CellPreview cell={cell} project={project} />
                                                </MovableAndResizableComponent>
                                            ))
                                    ) : (
                                        <div className="editor-no-page">
                                            <span className="editor-no-page-icon">📭</span>
                                            <span className="editor-no-page-text">Page vide</span>
                                            <span className="editor-no-page-hint">
                                                Utilisez la barre d'outils pour ajouter des éléments
                                            </span>
                                        </div>
                                    )
                                ) : (
                                    <div className="editor-no-page">
                                        <span className="editor-no-page-icon">👈</span>
                                        <span className="editor-no-page-text">Aucune page sélectionnée</span>
                                        <span className="editor-no-page-hint">
                                            Sélectionnez une page dans la barre latérale
                                        </span>
                                    </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                <aside className="editor-properties">
                    <div className="editor-properties-header">
                        <span className="editor-properties-title">Propriétés</span>
                    </div>
                    <div className="editor-properties-content">
                        {selectedCell ? (
                            <CellProperties 
                                cell={currentPage?.cells?.find(c => c.id === selectedCell)}
                                handleDeleteCell={handleDeleteCell}
                                onPositionChange={handlePropertyPositionChange}
                                onSizeChange={handlePropertySizeChange}
                                project={project}
                                handleUpdateCellProperties={handleUpdateCellProperties}
                            />
                        ) : currentPage ? (
                            <PageProperties page={currentPage} screenSize={project?.screen_size} project={project} setProject={setProject} setCurrentPage={setCurrentPage} />
                        ) : (
                            <div className="editor-no-page">
                                <span className="editor-no-page-text">
                                    Sélectionnez un élément pour voir ses propriétés
                                </span>
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            <footer className="editor-footer">
                <div className="editor-footer-left">
                    <div className="editor-footer-item">
                        <span className="editor-footer-dot"></span>
                        <span>Connecté</span>
                    </div>
                    <div className="editor-footer-item">
                        <span>
                            {project?.screen_size?.width} × {project?.screen_size?.height}
                        </span>
                    </div>
                </div>
                <div className="editor-footer-right">
                    <div className="editor-footer-item">
                        <span>{project?.pages?.length || 0} page{(project?.pages?.length || 0) > 1 ? 's' : ''}</span>
                    </div>
                    {currentPage && (
                        <div className="editor-footer-item">
                            <span>{currentPage.cells?.length || 0} cellule{(currentPage.cells?.length || 0) > 1 ? 's' : ''}</span>
                        </div>
                    )}
                </div>
            </footer>
        </div>
    );
}

function CellPreview({ cell, project }) {
    if (!cell) return null;

    const previewStyles = {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        overflow: 'hidden'
    };

    switch (cell.type) {
        case 'text':
            return (
                <TextCell {...cell} />
            );
        case 'image':
            return (
                <ImageCell {...cell} />
            );
        case 'video':
            return (
                <VideoCell {...cell} />
            );
        case 'button':
            return (
                <ButtonCell {...cell} />
            );
        case 'carousel':
            return (
                <CarouselCell {...cell} />
            );
        case 'form':
            return (
                <FormCell {...cell} />
            );
        case 'iframe':
            return (
                <IframeCell {...cell} />
            );
        case 'header':
            return (
                <HeaderCell {...cell} />
            );
        case 'html':
            return (
                <HtmlCell {...cell} />
            );
        case 'menu':
            return (
                <MenuCell {...cell} products={project?.products || []} />
            );
        case 'scheduled':
            return (
                <ScheduleCell {...cell} />
            );
        case 'pluracore':
            return (
                <PluracoreCell {...cell} />
            );
        case 'clock':
            return (
                <ClockCell {...cell} />
            );
        case 'review':
            return (
                <ReviewCell {...cell} />
            );
        case 'map':
            return (
                <MapCell {...cell} />
            );
        case 'commands':
            return (
                <CommandCell {...cell} products={project?.products || []} />
            );
        default:
            return (
                <CellWrapper {...cell} />
            );
    }
}

function CellProperties({ cell, onPositionChange, onSizeChange, handleDeleteCell, project, handleUpdateCellProperties }) {
    if (!cell) return null;

    return (
        <>
            <div className="property-section">
                <div className="property-section-title">Position</div>
                <div className="property-row">
                    <span className="property-label">X</span>
                    <input 
                        type="number" 
                        className="property-input property-input-small" 
                        value={cell.position_x || 0}
                        onChange={(e) => onPositionChange(cell.cell_id, { x: parseInt(e.target.value) || 0, y: cell.position_y })}
                    />
                    <span className="property-label">Y</span>
                    <input 
                        type="number" 
                        className="property-input property-input-small" 
                        value={cell.position_y || 0}
                        onChange={(e) => onPositionChange(cell.cell_id, { x: cell.position_x, y: parseInt(e.target.value) || 0 })}
                    />
                </div>
            </div>

            <div className="property-section">
                <div className="property-section-title">Dimensions</div>
                <div className="property-row">
                    <span className="property-label">L</span>
                    <input 
                        type="number" 
                        className="property-input property-input-small" 
                        value={cell.size_x || 0}
                        onChange={(e) => onSizeChange(cell.cell_id, { width: parseInt(e.target.value) || 1, height: cell.size_y })}
                    />
                    <span className="property-label">H</span>
                    <input 
                        type="number" 
                        className="property-input property-input-small" 
                        value={cell.size_y || 0}
                        onChange={(e) => onSizeChange(cell.cell_id, { width: cell.size_x, height: parseInt(e.target.value) || 1 })}
                    />
                </div>
            </div>

            <div className="property-section">
                <div className="property-section-title">Centrer</div>
                <div className="property-row">
                    <button 
                        className="property-button property-button-center" 
                        onClick={() => {
                            const previewWidth = project.screen_size.ratio_x * 50;
                            onPositionChange(cell.cell_id, { 
                                x: Math.round((previewWidth - cell.size_x) / 2), 
                                y: cell.position_y 
                            });
                        }}
                    >
                        Horizontal
                    </button>
                    <button 
                        className="property-button property-button-center" 
                        onClick={() => {
                            const previewHeight = project.screen_size.ratio_y * 50;
                            onPositionChange(cell.cell_id, { 
                                x: cell.position_x, 
                                y: Math.round((previewHeight - cell.size_y) / 2) 
                            });
                        }}
                    >
                        Vertical
                    </button>
                </div>
            </div>

            <div className="property-section">
                <div className="property-section-title">Index / Calque</div>
                <div className="property-row">
                    <span className="property-label">Index</span>
                    <input type="number" className="property-input property-input-small" value={cell.index || 0} onChange={(e) => handleUpdateCellProperties(cell.cell_id, {index: parseInt(e.target.value), type: cell.type})}/>
                </div>
            </div>

            <div className="property-section">
                <div className="property-section-title">Type</div>
                <div className="property-row">
                    <span style={{ 
                        padding: '0.5rem 0.75rem', 
                        background: 'rgba(99, 102, 241, 0.1)',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        color: '#818cf8',
                        textTransform: 'capitalize'
                    }}>
                        {cell.type || 'Inconnu'}
                    </span>
                </div>
            </div>

            <div className="property-section">
                <div className="property-section-title">Actions</div>
                <div className="property-row">
                    <span className="property-label">Action log</span>
                    <br/>
                    <input type="text" className="property-input property-input-small" value={cell.action_log || ''} onChange={(e) => handleUpdateCellProperties(cell.cell_id, {action_log: e.target.value, type: cell.type})}/>
                </div>
                <div className="property-row">
                    <span className="property-label">Link to</span>
                    <br/>
                    <select className="property-input property-input-small" value={cell.link_to || ''} onChange={(e) => handleUpdateCellProperties(cell.cell_id, {link_to: e.target.value, type: cell.type})}>
                        <option value="">None</option>
                        {project.pages.filter(page => page.id !== cell.page_id).map(page => (
                            <option value={page.id} key={page.id}>{page.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {
                cell.type === 'text' && (
                    <TextCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                ) 
            }
            {
                cell.type === 'form' && (
                    <FormCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'image' && (
                    <ImageCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'video' && (
                    <VideoCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'iframe' && (
                    <IframeCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'carousel' && (
                    <CarouselCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'button' && (
                    <ButtonCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'header' && (
                    <HeaderCellProperties cell={cell} onChange={handleUpdateCellProperties} pages={project.pages}/>
                )
            }
            {
                cell.type === 'html' && (
                    <HtmlCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'menu' && (
                    <MenuCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'scheduled' && (
                    <ScheduledCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'clock' && (
                    <ClockCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'review' && (
                    <ReviewCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                cell.type === 'map' && (
                    <MapCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }
            {
                (cell.type === 'command' || cell.type === 'commands') && (
                    <CommandCellProperties cell={cell} onChange={handleUpdateCellProperties}/>
                )
            }

            <div className="property-section">
                <div className="property-section-title">Delete Cell</div>
                <div className="property-row">
                    <button className="property-button-delete" onClick={() => handleDeleteCell(cell.cell_id)}>Delete</button>
                </div>
            </div>
        </>
    );
}

function PageProperties({ page, screenSize, project, setProject, setCurrentPage }) {
    if (!page) return null;

    const handleUpdatePageProperties = async (pageId, properties) => {
        try {
            const next = { ...page, ...properties };
            await PagesQueries.updatePage(
                pageId,
                next.name,
                next.background_type,
                next.background_color,
                next.background_image_uri,
                next.background_video_uri
            );
            setProject({ ...project, pages: project.pages.map(p => p.id === pageId ? { ...p, ...properties } : p) });
            setCurrentPage(next);
        } catch (error) {
            console.error("Erreur lors de la mise à jour des propriétés de la page:", error);
        }
    };

    return (
        <>
            <div className="property-section">
                <div className="property-section-title">Page</div>
                <div className="property-row">
                    <span className="property-label">Nom</span>
                    <input 
                        type="text" 
                        className="property-input" 
                        value={page.name || ''}
                        readOnly
                    />
                </div>
            </div>

            {screenSize && (
                <div className="property-section">
                    <div className="property-section-title">Écran</div>
                    <div className="property-row">
                        <span className="property-label">Taille</span>
                        <span style={{ fontSize: '0.85rem', color: '#f1f5f9' }}>
                            {screenSize.width} × {screenSize.height}
                        </span>
                    </div>
                    <div className="property-row">
                        <span className="property-label">Ratio</span>
                        <span style={{ fontSize: '0.85rem', color: '#f1f5f9' }}>
                            {screenSize.ratio_x}:{screenSize.ratio_y}
                        </span>
                    </div>
                    <div className="property-row">
                        <span className="property-label">Background type</span>
                        <select className="property-input property-input-small" value={page.background_type || 'color'} onChange={(e) => handleUpdatePageProperties(page.id, {background_type: e.target.value})}>
                            <option value="color">Couleur</option>
                            <option value="image">Image</option>
                            <option value="video">Vidéo</option>
                        </select>
                    </div>
                    <div className="property-row">
                        <span className="property-label">Background color</span>
                        <ColorPicker value={page.background_color || '#ffffff'} onChange={(color) => handleUpdatePageProperties(page.id, {background_color: color})}/>
                    </div>
                    <div className="property-row">
                        <span className="property-label">Background image</span>
                        <ImagePicker value={page.background_image_uri || ''} onChange={(image) => handleUpdatePageProperties(page.id, {background_image_uri: image})}/>
                    </div>
                    <div className="property-row">
                        <span className="property-label">Background video</span>
                        <VideoPicker value={page.background_video_uri || ''} onChange={(video) => handleUpdatePageProperties(page.id, {background_video_uri: video})}/>
                    </div>
                </div>
            )}

            <div className="property-section">
                <div className="property-section-title">Statistiques</div>
                <div className="property-row">
                    <span className="property-label">Cellules</span>
                    <span style={{ fontSize: '0.85rem', color: '#f1f5f9' }}>
                        {page.cells?.length || 0}
                    </span>
                </div>
            </div>
        </>
    );
}
